import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLiff } from "../../../liff/index.js";
import { fetchEvent, type EventData } from "../../../liff/api.js";
import { MobileShell, Card, CardHeader, MobileSpinner } from "../../../liff/components/Mobile.js";

export function EventPlaceholderPage(): React.ReactElement {
  const { eventId } = useParams<{ eventId: string }>();
  const { session } = useLiff();
  const [data, setData] = useState<EventData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session || !eventId) return;
    fetchEvent(eventId, session.session_token)
      .then(setData)
      .catch((e: unknown) => setError((e as Error).message));
  }, [session, eventId]);

  if (error) return (
    <MobileShell title="無法載入巡邏事件"><Card><p className="text-sm text-danger">{error}</p></Card></MobileShell>
  );
  if (!data) return <MobileSpinner />;

  const sdm = data.sdm_verification;
  const verifyOk = sdm.cmac_verified && sdm.counter_progression === "ok";

  return (
    <MobileShell title={data.location_label} subtitle={`${data.site_name} · ${data.officer_name} · ${data.occurred_at_local}`}>
      <Card>
        <CardHeader title="標籤驗證" hint={verifyOk ? "✓ 通過" : "✗ 異常"} />
        <ul className="divide-y divide-border-subtle -my-2">
          <Row k="CMAC 驗證" v={sdm.cmac_verified ? "通過" : "失敗"} ok={sdm.cmac_verified} />
          <Row k="Counter" v={`${sdm.counter}（${sdm.counter_progression}）`} ok={sdm.counter_progression === "ok"} />
          <Row k="UID" v={sdm.picc_uid_redacted} mono />
          <Row k="Keys" v={sdm.keys_used} mono />
        </ul>
      </Card>

      <Card>
        <CardHeader title="任務" hint={`${data.tasks.filter((t) => t.status === "done").length}/${data.tasks.length}`} />
        <ul className="divide-y divide-border-subtle -my-2">
          {data.tasks.map((t) => (
            <li key={t.id} className="py-2 flex items-center justify-between gap-2">
              <span className="text-sm">{t.label}</span>
              <span className={`text-[10px] uppercase tracking-wider shrink-0 ${
                t.status === "done" ? "text-success" : t.status === "skipped" ? "text-text-muted" : "text-danger"
              }`}>
                {t.status === "done" ? "完成" : t.status === "skipped" ? "略過" : "未完成"}
              </span>
            </li>
          ))}
        </ul>
      </Card>

      {data.photos.length > 0 && (
        <Card>
          <CardHeader title="照片" hint={`${data.photos.length} 張`} />
          <div className="grid grid-cols-2 gap-2">
            {data.photos.map((p) => (
              <a key={p.id} href={p.full_url} target="_blank" rel="noopener noreferrer" className="block aspect-[4/3] rounded-lg overflow-hidden bg-bg-raised">
                <img src={p.thumbnail_url} alt={p.id} loading="lazy" className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <CardHeader title="Checklist" hint={`${data.checklist.length} 項`} />
        <ul className="divide-y divide-border-subtle -my-2">
          {data.checklist.map((c, i) => (
            <li key={i} className="py-2 flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                c.status === "ok" ? "bg-success" : c.status === "fail" ? "bg-danger" : "bg-text-muted"
              }`} aria-hidden />
              <span className="text-sm">{c.item}</span>
            </li>
          ))}
        </ul>
      </Card>
    </MobileShell>
  );
}

function Row({ k, v, ok, mono }: { k: string; v: string; ok?: boolean; mono?: boolean }): React.ReactElement {
  return (
    <li className="py-2 flex items-center justify-between gap-2">
      <span className="text-xs text-text-secondary">{k}</span>
      <span className={`${mono ? "font-mono" : ""} text-sm ${ok === false ? "text-danger" : ok === true ? "text-success" : "text-text-primary"}`}>
        {v}
      </span>
    </li>
  );
}
